import { useEffect, useState } from "react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { COMPANY_ROLLUPS, readCount } from "utilities/companyRollups";

/**
 * SOUND-643 Phase 3 — related-record counts for the company record home.
 *
 * Four fixed requests, one per entity type, regardless of how many records
 * exist. That is NOT the N+1 SOUND-295 is about — that one fetches per visible
 * ROW on a list page.
 *
 * `limit=1` on the three paginated endpoints, because only `pagination.total` is
 * wanted; nothing reads the row. `/files` gained pagination in SOUND-661 but it
 * is OPT-IN — omitting page/limit returns the full list plus a real total — so
 * it is still called without `limit=1` and readCount takes `pagination.total`.
 * Adding `limit=1` here would work too; leaving it off keeps the call identical
 * for a caller that wants the rows.
 *
 * Failures are per-endpoint: one 403 leaves that count unknown (rendered as a
 * dash) rather than blanking the whole card. A partner without `mid-manager`
 * legitimately cannot see MIDs, and that should not cost them the rest.
 */
export function useCompanyRollups(companyId) {
  const auth = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId || !auth?.user) return;
    let cancelled = false;
    setLoading(true);
    setCounts({});

    Promise.all(
      COMPANY_ROLLUPS.map(async (r) => {
        try {
          const qs = `?companyId=${encodeURIComponent(companyId)}${
            r.path === "files" ? "" : "&limit=1"
          }`;
          const res = await makeApiRequest(
            "get",
            `${r.path}${qs}`,
            auth.user,
            effectiveCompanyId,
            null
          );
          return [r.key, readCount(res?.data)];
        } catch {
          // Leave it unknown rather than asserting zero.
          return [r.key, undefined];
        }
      })
    ).then((entries) => {
      if (cancelled) return;
      const next = {};
      for (const [key, value] of entries) {
        if (typeof value === "number") next[key] = value;
      }
      setCounts(next);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [companyId, auth?.user, effectiveCompanyId]);

  return { counts, loading };
}
